import { useQueryClient } from "@tanstack/react-query";
import { MoreVertical } from "lucide-react";
import { FC, useState } from "react";
import { useIntl } from "react-intl";
import { CategoryFolder } from "~/api/model";
import {
  getListOrganizationCategoryFoldersQueryKey,
  useUpdateOrganizationCategoryFolder,
} from "~/api/organization/organization";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useOrganizationParams } from "~/helpers";
import { CategoryListItem } from "./CategoriesListItem";
import { ColorPicker } from "./ColorPicker";
import { DeleteFolderAlertDialog } from "./DeleteFolderAlertDialog";
import EditFolderModal from "./EditFolderModal";
interface FolderListItemProps {
  folder: CategoryFolder;
}

export const FolderListItem: FC<FolderListItemProps> = ({ folder }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: updateFolder } = useUpdateOrganizationCategoryFolder();
  const queryClient = useQueryClient();

  const handleUpdateFolderColor = async (color: string) => {
    await updateFolder({
      organizationId: organizationId,
      folderId: folder.id,
      data: { color },
    });
    queryClient.invalidateQueries({
      queryKey: getListOrganizationCategoryFoldersQueryKey(organizationId),
    });
  };

  const dropdownMenuItems = [
    {
      label: intl.formatMessage({ id: "folderListItem.edit" }),
      onClick: () => {
        setIsEditModalOpen(true);
        setIsDropdownOpen(false);
      },
    },
    {
      label: intl.formatMessage({ id: "folderListItem.delete" }),
      onClick: () => {
        setIsDeleteDialogOpen(true);
        setIsDropdownOpen(false);
      },
      className: "text-destructive",
    },
  ];

  return (
    <>
      <div
        className="col-span-2 inline-flex flex-col justify-start items-start gap-5 rounded-[16px] border-2 p-3"
        style={{ borderColor: folder.color || "currentColor" }}
      >
        <div className="self-stretch inline-flex justify-start items-start gap-3">
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-2">
            <div className="self-stretch inline-flex justify-start items-start gap-2">
              <div onClick={(e) => e.stopPropagation()}>
                <ColorPicker
                  value={folder.color}
                  onChange={handleUpdateFolderColor}
                />
              </div>
              <div className="flex-1 text-foreground text-base font-semibold leading-6 line-clamp-1">
                {folder.name}
              </div>
            </div>
            {folder.description && (
              <div className="self-stretch text-muted-foreground text-sm font-normal leading-5">
                {folder.description}
              </div>
            )}
          </div>
          <div className="p-0.5 flex justify-start items-center gap-2.5">
            <DropdownMenu
              open={isDropdownOpen}
              onOpenChange={setIsDropdownOpen}
            >
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-5 w-5 p-0">
                  <MoreVertical className="h-5 w-5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {dropdownMenuItems.map(({ label, onClick, className }) => (
                  <DropdownMenuItem
                    key={label}
                    onClick={onClick}
                    className={className}
                  >
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Categories Grid */}
        {folder.categories.length > 0 && (
          <div className="w-full grid grid-cols-2 gap-4">
            {folder.categories.map((category) => (
              <CategoryListItem key={category.id} category={category} />
            ))}
          </div>
        )}
      </div>

      {isEditModalOpen && (
        <EditFolderModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          folder={folder}
        />
      )}
      {isDeleteDialogOpen && (
        <DeleteFolderAlertDialog
          open={isDeleteDialogOpen}
          setOpen={setIsDeleteDialogOpen}
          folderId={folder.id}
          hasCategories={folder.categories.length > 0}
        />
      )}
    </>
  );
};
